import type { FC, MouseEvent } from 'react'
import { memo, useEffect, useLayoutEffect, useRef, useState } from 'react'

import { type SpacingProps } from '@material-ui/system'
import { debounce, throttle } from 'lodash'

import { InputLabel } from '../Form'
import { PaginationLabelStyled } from './styles/PaginationLabelStyled'
import { PaginationStyled } from './styles/PaginationStyled'
import { PaginationTextFieldStyled } from './styles/PaginationTextFieldStyled'
import { IconButton } from '../../elements/Button'
import { IconChevronLeft, IconChevronRight } from '@ui/elements/Icon'

export interface IPaginationProps extends SpacingProps {
  currentPage?: number
  numPages?: number
  paginationLabel?: string
  /**
   * Id of the page number input, which the "Page" label points at. Give each
   * pager on a screen its own id. Defaults to `currentPageInput`.
   */
  inputId?: string
  onPageChange?(currentPage: number, e?: MouseEvent<HTMLElement>): void
}

interface PendingFocus {
  trigger: HTMLButtonElement
  targetSelector: '.previous-button' | '.next-button'
}

export const Pagination: FC<IPaginationProps> = memo((props) => {
  const {
    currentPage,
    numPages,
    paginationLabel = 'Page',
    inputId = 'currentPageInput',
    onPageChange,
    ...otherProps
  } = props
  const [pageValue, setPageValue] = useState<number | ''>(currentPage || 1)
  const paginationRef = useRef<HTMLDivElement>(null)
  const pendingFocusRef = useRef<PendingFocus | null>(null)

  const isPreviousDisabled = currentPage === 1 || pageValue === 1
  const isNextDisabled = currentPage === numPages || pageValue === numPages

  // Paging onto the first or last page disables the focused button, and the
  // browser drops focus to the page body. Focus moves to the opposite button
  // once it renders enabled, unless the user has moved focus on by then.
  // IconButton forwards no ref, hence the selector.
  useLayoutEffect(() => {
    const pending = pendingFocusRef.current
    if (!pending) return

    const { activeElement } = document
    if (
      activeElement &&
      activeElement !== document.body &&
      activeElement !== pending.trigger
    ) {
      pendingFocusRef.current = null
      return
    }

    const target = paginationRef.current?.querySelector<HTMLButtonElement>(
      pending.targetSelector
    )
    if (target && !target.disabled) {
      pendingFocusRef.current = null
      target.focus()
    }
  })

  useEffect(() => {
    if (pageValue !== currentPage) {
      setPageValue(currentPage)
    }
  }, [currentPage])

  const handlePageChange = debounce((validatedInput, prevCurrentPage) => {
    setPageValue(validatedInput)
    if (validatedInput !== prevCurrentPage) {
      onPageChange(validatedInput)
    }
  }, 250)

  const handlePageClick = throttle((validatedInput) => {
    onPageChange(validatedInput)
  }, 250)

  const keepFocusInPager = (
    event: MouseEvent<HTMLButtonElement>,
    targetSelector: PendingFocus['targetSelector']
  ) => {
    if (event.currentTarget === document.activeElement) {
      pendingFocusRef.current = { trigger: event.currentTarget, targetSelector }
    }
  }

  const handlePreviousClick = (event: MouseEvent<HTMLButtonElement>) => {
    const validatedInput = validateInput(+pageValue - 1)
    if (validatedInput !== pageValue) {
      if (validatedInput === 1) {
        keepFocusInPager(event, '.next-button')
      }
      setPageValue(validatedInput)
      handlePageClick(validatedInput)
    }
  }

  const handleNextClick = (event: MouseEvent<HTMLButtonElement>) => {
    const validatedInput = validateInput(+pageValue + 1)
    if (validatedInput !== pageValue) {
      if (validatedInput === numPages) {
        keepFocusInPager(event, '.previous-button')
      }
      setPageValue(validatedInput)
      handlePageClick(validatedInput)
    }
  }

  const handleInputChange = (event) => {
    if (event.target.value === '') {
      setPageValue(event.target.value)
    } else {
      const prevCurrentPage = pageValue
      const validatedInput = validateInput(event.target.value)
      setPageValue(validatedInput)
      handlePageChange(validatedInput, prevCurrentPage)
    }
  }

  const handleDefocus = () => {
    if (pageValue === '') {
      setPageValue(1)
      onPageChange(1)
    }
  }

  const validateInput = (input) => {
    if (isNaN(input)) {
      return pageValue
    }

    if (input < 1) {
      return 1
    }

    if (input > numPages) {
      return numPages
    }

    return Number(input)
  }

  return (
    <PaginationStyled ref={paginationRef} {...otherProps}>
      <IconButton
        aria-label='Go to previous page'
        className='previous-button'
        iconComponent={<IconChevronLeft />}
        disabled={isPreviousDisabled}
        onClick={handlePreviousClick}
        type='button'
      />
      <PaginationLabelStyled>
        <InputLabel display='inline' htmlFor={inputId}>
          {paginationLabel}
        </InputLabel>
      </PaginationLabelStyled>
      <PaginationTextFieldStyled
        className='input-field'
        onBlur={handleDefocus}
        value={pageValue}
        onChange={handleInputChange}
        id={inputId}
      />
      <PaginationLabelStyled>of {numPages}</PaginationLabelStyled>
      <IconButton
        aria-label='Go to next page'
        className='next-button'
        iconComponent={<IconChevronRight />}
        disabled={isNextDisabled}
        onClick={handleNextClick}
        type='button'
      />
    </PaginationStyled>
  )
})

Pagination.defaultProps = { currentPage: 1, numPages: 1 }
Pagination.displayName = 'Pagination'
